import { PolicyInsightsRuleBased } from "../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { SimulationChangeType, SimulationMimshakData, FundSumDropMap } from "../../interfaces/DTO/simulation";
import { AdjustedProductType, ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { PensionFeeExpenseType } from "../../interfaces/DTO/mislaka_xml_pension_models";
import { formatPercentage } from "../../utils/namesFormater";
import { fundService } from "../fundService";
import { mimshakDataService } from "../mimshakDataService";
import { MimshakProductUtil } from "../MimshakProductUtil";
import { MimshakPensionService } from "../mimshakPensionService";
import { computeFeeTradeoffMetrics, decideFeeSwitch } from "../../utils/PensionCalculatorUtil";
import { AgentProducerCode } from "../../interfaces/DTO/agent/agentProducerCode";
import { Contact } from "../../interfaces/DTO/crm/contact";

const EXECUTIVE_INSURANCE_TYPES = new Set([
  AdjustedProductType.ACTIVE_MANAGERS_INSURANCE,
  AdjustedProductType.INACTIVE_MANAGERS_INSURANCE,
]);

export const NO_FUND_SELECTED_LABEL = "לא נבחר מסלול חדש";

/**
 * Returns the saving fee that actually applies after a change.
 *
 * Business rule:
 *  - Handler changed  → always use the new calculated fee (moving to a new provider,
 *    whatever fee was negotiated applies unconditionally).
 *  - Same handler     → only use the new fee if it is strictly lower than the original
 *    (the calculated fee is a proposal; a higher proposal is never applied).
 *  - No original      → new policy, use the fee as-is.
 */
export function getEffectiveSavingFee(
  originalFee: number | null | undefined,
  calculatedFee: number | null | undefined,
  handlerChanged: boolean
): number | null {
  if (calculatedFee == null) return originalFee ?? null;
  if (originalFee == null || handlerChanged) return calculatedFee;
  return calculatedFee < originalFee ? calculatedFee : originalFee;
}

/**
 * Pension-specific fee display logic.
 * - Manual change (CHANGE_FEE in changeTypes): always show the new fee, regardless of direction.
 * - Auto-calculated: only show if the combined saving+deposit tradeoff recommends SWITCH.
 */
export function getEffectivePensionFee(
  original: { saving_fee?: number | null; deposit_fee?: number | null; funds_sum?: number | null; deposit_amount?: number | null } | null | undefined,
  newSavingFee: number | null | undefined,
  newDepositFee: number | null | undefined,
  changeTypes: string[]
): number | null {
  const isManual = changeTypes.includes("CHANGE_FEE");
  const isHandlerChanged = changeTypes.includes("CLOSE_AND_MOVE");
  if (isHandlerChanged) return newSavingFee ?? null;
  if (newSavingFee == null) return original?.saving_fee ?? null;
  if (isManual) return newSavingFee;

  const origSaving = original?.saving_fee ?? 0;
  const origDeposit = original?.deposit_fee ?? 0;
  const newDeposit = newDepositFee ?? origDeposit;
  const metrics = computeFeeTradeoffMetrics(
    { current_assets: original?.funds_sum ?? 0, monthly_contribution: original?.deposit_amount ?? 0 },
    { asset_fee_rate: origSaving / 100, contribution_fee_rate: origDeposit / 100 },
    { asset_fee_rate: newSavingFee / 100, contribution_fee_rate: newDeposit / 100 }
  );
  return decideFeeSwitch(metrics).decision === "SWITCH" ? newSavingFee : (original?.saving_fee ?? null);
}

export class SimulationChangeDetectionUtil {
    static detectChangeTypesFromRowPair = (
    original: MimshakData,
    modified: MimshakData | undefined,
    fundSumDropMap: FundSumDropMap
    ): SimulationChangeType[] => {
    if (!modified) return [SimulationChangeType.UNCHANGED];
       
    const originalPathCodes = [...new Set(MimshakProductUtil.getPathCodesFromProduct(original.data?.products?.[0]))];
    const modifiedPathCodes = [...new Set(MimshakProductUtil.getPathCodesFromProduct(modified?.data?.products?.[0]))];

    const originalFunds = originalPathCodes.map(code => fundService.getFundFromCacheById(code)).filter(Boolean);
    const modifiedFundsRaw = modifiedPathCodes.map(code => fundService.getFundFromCacheById(code)).filter(Boolean);
    const modifiedFunds = modifiedFundsRaw.filter(
        modFund => !originalFunds.some(origFund => origFund?.fund_id === modFund?.fund_id)
    );
    const removedFunds = originalFunds.filter(
        origFund => !modifiedFundsRaw.some(modFund => modFund?.fund_id === origFund?.fund_id)
    );

    // Check if there are any fund additions (deposit, transfer, or recurrent)
    const additions = fundSumDropMap[original.policy_number];
    const hasAdditions = !!(additions?.deposit || additions?.transfer || additions?.recurrent || additions?.withdrawal);

    // Treat saving_fee changing to null as "no data" rather than a real change —
    // it means overrides are missing for the handler, not that the fee was modified.
    const feeChanged = modified?.saving_fee !== null &&
        original.saving_fee !== modified?.saving_fee;

    const fundsSumReduced =
        original.funds_sum != null && modified?.funds_sum != null &&
        modified.funds_sum < original.funds_sum &&
        original.handler_code === modified?.handler_code;

    const showChange =
        feeChanged ||
        original.handler_code !== modified?.handler_code ||
        modifiedFunds.length > 0 ||
        removedFunds.length > 0 ||
        (additions?.recurrent !== undefined && (original.recurrent_deposit ?? 0) !== additions.recurrent) ||
        hasAdditions ||
        fundsSumReduced;

    if (!showChange) return [SimulationChangeType.UNCHANGED];

    const changeTypes: SimulationChangeType[] = [];

    if (original.handler_code !== modified?.handler_code) {
        changeTypes.push(SimulationChangeType.CLOSE_AND_MOVE);
        if (modifiedFunds.length === 0) {
        modifiedFunds.push({
            fund_id: "*",
            fund_name: NO_FUND_SELECTED_LABEL,
            sub_specialization: "",
        } as any);
        }
    }

    if ((modifiedFunds.length > 0 || removedFunds.length > 0) && original.handler_code === modified?.handler_code) {
        changeTypes.push(SimulationChangeType.CHANGE_FUND);
    }

    // Check for fund additions (deposit)
    // Note: additions was already retrieved above in the hasAdditions check
    if (additions?.deposit) {
        changeTypes.push(SimulationChangeType.DEPOSIT);
    }
    if (additions?.withdrawal) {
        changeTypes.push(SimulationChangeType.WITHDRAWAL);
    }
    // For transfer and recurrent types, we don't add specific change types
    // as recurrent is tracked via recurrent_deposit field below
    // and transfer is typically combined with other change types

    if (
        modified?.saving_fee !== undefined &&
        original.saving_fee !== undefined &&
        modified.saving_fee < original.saving_fee &&
        original.handler_code === modified.handler_code
    ) {
        changeTypes.push(SimulationChangeType.CHANGE_FEE);
    }

    if (
        original.handler_code === modified?.handler_code &&
        original.funds_sum != null && modified?.funds_sum != null &&
        modified.funds_sum < original.funds_sum
    ) {
        changeTypes.push(SimulationChangeType.PARTIAL_TRANSFER);
    }

    // בדיקת שינוי בהוראת קבע חודשית — שינוי רק אם המפה מכילה ערך recurrent
    const originalRecurrent = original.recurrent_deposit ?? 0;
    const rawRecurrent = additions?.recurrent;
    const isStopped = rawRecurrent === null;
    const modifiedRecurrent = isStopped ? 0 : (rawRecurrent !== undefined && rawRecurrent !== null ? rawRecurrent : originalRecurrent);
    if (isStopped && originalRecurrent > 0) {
        changeTypes.push(SimulationChangeType.STOP_RECURRENT_DEPOSIT);
    } else if (originalRecurrent !== modifiedRecurrent) {
        if (originalRecurrent === 0 && modifiedRecurrent > 0) {
            changeTypes.push(SimulationChangeType.NEW_RECURRENT_DEPOSIT);
        } else {
            changeTypes.push(SimulationChangeType.CHANGE_RECURRENT_DEPOSIT);
        }
    }

    if (changeTypes.length === 0) {
        changeTypes.push(SimulationChangeType.UNCHANGED);
    }

    return changeTypes;
    };


    /**
     * Resolves which funds changed between original and modified rows.
     * Returns the filtered modified funds, or a sentinel entry when handler
     * changed but no matching fund was found.
     * Used by SummaryChangesTable and SummaryChangesVisual to avoid
     * duplicating this logic in UI components.
     */
    static resolveFundChange = (
        original: MimshakData | undefined,
        modified: MimshakData | undefined
    ): { funds: any[]; removedFunds: any[]; noFundSelected: boolean } => {
        if (!modified) return { funds: [], removedFunds: [], noFundSelected: false };

        const originalPaths = original
            ? (original.data?.products ?? []).flatMap(p => MimshakProductUtil.getMergedPathsFromProduct(p))
            : [];
        const modifiedPaths = (modified.data?.products ?? []).flatMap(p => MimshakProductUtil.getMergedPathsFromProduct(p));

        const originalFunds = originalPaths.map(p => ({
            ...(fundService.getFundFromCacheById(p.path_code) ?? { fund_id: p.path_code, fund_name: p.path_name ?? String(p.path_code) } as any),
            _amount: p.accumulated_amount ?? 0,
            _depositPct: p.deposit_percentage,
        }));
        const modifiedFundsRaw = modifiedPaths.map(p => {
            const fund = fundService.getFundFromCacheById(p.path_code);
            if (!fund) return null;
            return { ...fund, _amount: p.accumulated_amount ?? 0, _depositPct: p.deposit_percentage };
        }).filter(Boolean);

        const modifiedFunds = modifiedFundsRaw.filter(f => {
            const matchesHandler = !modified.handler_code || String(f?.managing_corporation_legal_id) === String(modified.handler_code);
            const matchesProduct = !modified.product_type ||
                f?.product_type === modified.product_type ||
                (f?.product_type === ProductType.LIFE_INSURANCE_AND_PURE_SAVINGS &&
                    (modified.product_type === ProductType.LIFE_INSURANCE_SAVINGS ||
                     modified.product_type === ProductType.PURE_SAVINGS_POLICY));
            return matchesHandler && matchesProduct;
        });

        const removedFunds = originalFunds.filter(of => !modifiedFunds.some(mf => mf?.fund_id === of?.fund_id));

        const sameFunds =
            modifiedFunds.length === originalFunds.length &&
            modifiedFunds.every(mf => originalFunds.some(of => of?.fund_id === mf?.fund_id));

        if (sameFunds) return { funds: [], removedFunds: [], noFundSelected: false };

        const isHandlerChanged = original ? original.handler_code !== modified.handler_code : !!modified.handler_code;
        const noFundSelected = (isHandlerChanged || !original) && modifiedFunds.length === 0;

        if (noFundSelected) {
            return {
                funds: [{ fund_id: "*", fund_name: NO_FUND_SELECTED_LABEL, sub_specialization: "" }],
                removedFunds: [],
                noFundSelected: true,
            };
        }

        return { funds: modifiedFunds, removedFunds, noFundSelected: false };
    };

    static calculateHighlightedNote = (
    _changeTypes: SimulationChangeType[],
    _original: MimshakData,
    modified: MimshakData
    ): string | undefined => {
      const transfer = modified.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
      if (!transfer) return undefined;
      const isExecutive = EXECUTIVE_INSURANCE_TYPES.has(modified.adjusted_product_type as AdjustedProductType)
        || EXECUTIVE_INSURANCE_TYPES.has(transfer.transferring_fund_id as AdjustedProductType);
      if (!isExecutive) return undefined;

      const isCapitalOnly = !!(modified as MimshakData & { _capitalOnly?: boolean })._capitalOnly;
      const noteParts: string[] = ["ניוד ביטוח מנהלים"];
      if (isCapitalOnly) noteParts.push("רכיב הוני בלבד");

      const policy = modified.data?.products?.[0]?.policies_or_accounts?.[0];
      const sourceFeeDetails = policy?.management_fee_details ?? [];
      const sourceFees = sourceFeeDetails
        .filter((m: any) => m.expense_type === PensionFeeExpenseType.FROM_SAVINGS && m.management_fee_rate !== 0)
        .map((m: any) => m.management_fee_rate as number)
        .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i);
      const sourceFee = sourceFees.length > 0 ? sourceFees[0] : null;
      const newFee = modified.saving_fee ?? null;
      if (sourceFee != null && newFee != null && newFee < sourceFee) {
        noteParts.push(`להורדת דמי ניהול: מ־${formatPercentage(sourceFee)} ל־${formatPercentage(newFee)}`);
      }

      if (!isCapitalOnly && modified.data?.products) {
        const factors = MimshakPensionService.getUniqueGuaranteedConversionFactors(modified.data.products);
        if (factors.length > 0) {
          noteParts.push(`בניוד מאבדים את המקדם המובטח: ${factors.join(", ")}`);
        }
      }

      return noteParts.join(" | ");
    };

static convertModifiedToSimulationItems(
    originalRows: MimshakData[],
    modifiedRows: MimshakData[],
    fundSumDropMap: FundSumDropMap,
  ): SimulationMimshakData[] {
    return modifiedRows.map((modified) => {
        const original = originalRows.find((r) => r.policy_number === modified.policy_number)!;
        const additions = fundSumDropMap[modified.policy_number];
        const newRecurrent = additions?.recurrent ?? modified.recurrent_deposit ?? 0;
        const changeTypes = original
      ? this.detectChangeTypesFromRowPair(original, modified, fundSumDropMap)
      : newRecurrent > 0
        ? [SimulationChangeType.OPEN_NEW, SimulationChangeType.NEW_RECURRENT_DEPOSIT]
        : [SimulationChangeType.OPEN_NEW];
        const note: string | undefined = undefined; // auto-notes are computed at display time via calculateHighlightedNote
        return <SimulationMimshakData>{
        ...modified,
        id: undefined,
        deposit_amount: additions?.deposit ?? modified.deposit_amount,
        recurrent_deposit: additions?.recurrent ?? modified.recurrent_deposit,
        transfer_out: additions?.transfer ?? modified.transfer_out,
        withdrawal_amount: additions?.withdrawal ?? modified.withdrawal_amount,
        simulation_id: undefined,
        change_types: changeTypes,
        is_pre_advice: undefined,
        note,
        };
    });
    };

  /**
   * Checks whether the fund selection changed for a policy (with handler/product filter).
   */
  static hasFundChange(
    original: MimshakData | undefined,
    modified: MimshakData | undefined
  ): boolean {
    if (!original || !modified) return false;
    const originalPaths = MimshakProductUtil.getMergedPathsFromProduct(original.data?.products?.[0]);
    const modifiedPaths = MimshakProductUtil.getMergedPathsFromProduct(modified.data?.products?.[0]);
    const originalFunds = originalPaths.map(p => fundService.getFundFromCacheById(p.path_code)).filter(Boolean);
    const modifiedFundsRaw = modifiedPaths.map(p => fundService.getFundFromCacheById(p.path_code)).filter(Boolean);
    const modifiedFunds = modifiedFundsRaw.filter(f => {
      const matchesHandler = !modified.handler_code || String(f?.managing_corporation_legal_id) === String(modified.handler_code);
      const matchesProduct = !modified.product_type || f?.product_type === modified.product_type;
      return matchesHandler && matchesProduct;
    });
    return modifiedFunds.length > 0 && !(
      modifiedFunds.length === originalFunds.length &&
      modifiedFunds.every(mf => originalFunds.some(of => of?.fund_id === mf?.fund_id))
    );
  }

  /**
   * Calculates weighted exposure value from modified paths.
   * Returns null if no paths have accumulated_amount > 0.
   */
  static calcWeightedExposure(
    modifiedPaths: ReturnType<typeof MimshakProductUtil.getMergedPathsFromProduct>,
    getField: (f: ReturnType<typeof fundService.getFundFromCacheById>) => number | null | undefined
  ): number | null {
    let totalAmount = 0;
    let weighted = 0;
    for (const p of modifiedPaths) {
      const f = fundService.getFundFromCacheById(p.path_code);
      if (f && p.accumulated_amount > 0) {
        const pct = getField(f);
        if (pct != null) { weighted += pct * p.accumulated_amount; totalAmount += p.accumulated_amount; }
      }
    }
    return totalAmount > 0 ? weighted / totalAmount : null;
  }

  /**
   * Computes actual weighted exposure values for stock, currency, and foreign exposure
   * for a modified policy — only when a fund change has occurred.
   */
  static calcActualExposures(
    original: MimshakData | undefined,
    modified: MimshakData | undefined
  ): { stock: number | null; currency: number | null; foreign: number | null } {
    const noChange: { stock: number | null; currency: number | null; foreign: number | null } = { stock: null, currency: null, foreign: null };
    if (!this.hasFundChange(original, modified) || !modified) return noChange;
    const modifiedPaths = MimshakProductUtil.getMergedPathsFromProduct(modified.data?.products?.[0]);
    return {
      stock: this.calcWeightedExposure(modifiedPaths, f => f?.stock_market_percent ?? (f?.stock_market_exposure && f?.total_assets ? (f.stock_market_exposure / f.total_assets) * 100 : null)),
      currency: this.calcWeightedExposure(modifiedPaths, f => f?.foreign_currency_exposure_percent ?? null),
      foreign: this.calcWeightedExposure(modifiedPaths, f => f?.foreign_exposure_percent ?? null),
    };
  }

  static readonly AGENT_ASSIGNMENT_TITLES: Record<string, string> = {
    none: "ללא מינוי",
    full: "מינוי סוכן מלא",
    viewOnly: "מינוי לתצוגה בלבד",
  };

  static applyAgentCodes(
    items: SimulationMimshakData[],
    isPreAdvice: boolean,
    agentAssignmentMap: Record<string, "none" | "full" | "viewOnly">,
    contacts: Contact[],
    agentProducerCodes: AgentProducerCode[]
  ): SimulationMimshakData[] {
    return items.map((item) => {
      const resolved = this.resolveAgentAssignment(
        item.policy_number,
        item.contact_id,
        item.change_types ?? [],
        isPreAdvice,
        agentAssignmentMap,
        item.handler_code ?? null,
        contacts,
        agentProducerCodes
      );
      return {
        ...item,
        agent_code: resolved?.code ?? null,
        agent_title: resolved?.title ?? null,
        agent_missing_rev_code: resolved?.missingRevCode ?? false,
        agent_is_rev_client: resolved?.isRevClient ?? false,
      };
    });
  }

  static resolveAgentAssignment(
    policyNumber: string,
    contactId: number,
    changeTypes: SimulationChangeType[],
    isPreAdvice: boolean,
    agentAssignmentMap: Record<string, "none" | "full" | "viewOnly">,
    handlerCode: string | null,
    contacts: Contact[],
    agentProducerCodes: AgentProducerCode[]
  ): { title: string; code: string | null; isRevClient: boolean; missingRevCode: boolean } | null {
    const isNewOrTransfer = changeTypes?.some(
      (t) => t === SimulationChangeType.OPEN_NEW || t === SimulationChangeType.CLOSE_AND_MOVE
    );
    if (!isNewOrTransfer && !isPreAdvice) return null;

    const assignment = agentAssignmentMap[policyNumber] || "full";
    const title = this.AGENT_ASSIGNMENT_TITLES[assignment];
    const isRevClient = contacts.find((c) => c.id === contactId)?.rev_client === true;

    if (assignment === "none") {
      return { title, code: null, isRevClient, missingRevCode: false };
    }

    const { code, missingRevCode } = this.getAgentCodeForHandler(handlerCode, isRevClient, agentProducerCodes);
    return { title, code, isRevClient, missingRevCode };
  }

  static getAgentCodeForHandler(
    handlerCode: string | null,
    isRevClient: boolean,
    agentProducerCodes: AgentProducerCode[]
  ): { code: string | null; missingRevCode: boolean } {
    if (!handlerCode) return { code: null, missingRevCode: false };
    const codesForHandler = agentProducerCodes.filter((c) => c.handler_code === handlerCode);
    if (codesForHandler.length === 0) return { code: null, missingRevCode: isRevClient };

    if (isRevClient) {
      const revCode = codesForHandler.find((c) => c.shared_with_rev === true);
      if (revCode) return { code: revCode.code, missingRevCode: false };
      const defaultCode = codesForHandler.find((c) => c.is_default);
      return { code: defaultCode?.code ?? codesForHandler[0].code, missingRevCode: true };
    }

    const defaultCode = codesForHandler.find((c) => c.is_default);
    return { code: defaultCode?.code ?? codesForHandler[0].code, missingRevCode: false };
  }

  // Convert PolicyInsightsRuleBased[] to SimulationMimshakData[]
  static convertInsightsToSimulationItems(insights: PolicyInsightsRuleBased[]): SimulationMimshakData[] {
    return insights.map(insight => ({
      contact_id: insight.contact_id,
      product_type: insight.product_type,
      adjusted_product_type: insight.adjusted_product_type,
      policy_number: insight.policy_number,
      handler_code: insight.handler_code,
      funds_sum: insight.funds_sum || 0,
      saving_fee: insight.proposed_value || 0,
      change_types: [],
    } as SimulationMimshakData));
  }

}
